import type { ReadonlyURL } from "$app/state";
import { modulePath } from "@colossus/contracts/module";
import { LOCALE_PARAM } from "./locale.ts";
import { withParam } from "./scope.ts";
import type { ScopeParam } from "./scope.ts";
import { memberLensOf, onlyParams, viewParams } from "./view-index.ts";

const CARRIED_ALWAYS: readonly string[] = [LOCALE_PARAM];

export const viewPath = (tenantId: string, viewId: string): string =>
  modulePath(tenantId, "analytics", viewId);

const VIEW_PATH = /^\/t\/[^/]+\/analytics\/(.+)$/;

export const viewAtPath = (pathname: string): string | null => {
  const match = VIEW_PATH.exec(pathname);
  return match === null || match[1] === undefined ? null : match[1];
};

type ParamValue = string | readonly string[] | null;

export const hrefWithParams = (
  url: ReadonlyURL,
  entries: readonly (readonly [ScopeParam, ParamValue])[],
): string => {
  const params = entries.reduce(
    (carry, [name, value]) => withParam(carry, name, value),
    new URLSearchParams(url.search),
  );
  const search = params.toString();
  return search === "" ? url.pathname : `${url.pathname}?${search}`;
};

export const hrefWith = (url: ReadonlyURL, name: ScopeParam, value: ParamValue): string =>
  hrefWithParams(url, [[name, value]]);

export const hrefWithRange = (url: ReadonlyURL, since: string, until: string): string =>
  hrefWithParams(url, [
    ["since", since],
    ["until", until],
  ]);

export const viewHref = (url: ReadonlyURL, tenantId: string, viewId: string): string => {
  const params = onlyParams(url.searchParams, [...viewParams(viewId), ...CARRIED_ALWAYS]);
  params.sort();
  const search = params.toString();
  const path = viewPath(tenantId, viewId);
  return search === "" ? path : `${path}?${search}`;
};

export const memberLensHref = (
  url: ReadonlyURL,
  tenantId: string,
  fromViewId: string,
  userId: string,
): string => {
  const next = new URL(url.href);
  next.searchParams.set("user", userId);
  return viewHref(next, tenantId, memberLensOf(fromViewId));
};

export const filterHref = (
  url: ReadonlyURL,
  params: { readonly include: ScopeParam; readonly exclude: ScopeParam },
  mode: "include" | "exclude",
  ids: readonly string[],
): string => {
  const set = ids.length === 0 ? null : ids;
  return hrefWithParams(url, [
    [params.include, mode === "include" ? set : null],
    [params.exclude, mode === "exclude" ? set : null],
  ]);
};

export type CompareKind = "member" | "channel";

const COMPARE: Readonly<
  Record<CompareKind, { readonly view: string; readonly a: ScopeParam; readonly b: ScopeParam }>
> = {
  member: { view: "members/compare", a: "user", b: "userB" },
  channel: { view: "engagement/channels/compare", a: "channel", b: "channelB" },
};

export const compareHref = (
  url: ReadonlyURL,
  tenantId: string,
  kind: CompareKind,
  id: string,
  otherId: string,
): string => {
  const { view, a, b } = COMPARE[kind];
  const next = new URL(url.href);
  next.searchParams.set(a, id);
  next.searchParams.set(b, otherId);
  return viewHref(next, tenantId, view);
};

export const dropsEdits = (
  scope: string | readonly string[],
  to: { readonly url: URL } | null,
): boolean =>
  to === null ||
  ![scope]
    .flat()
    .some((path) => to.url.pathname === path || to.url.pathname.startsWith(`${path}/`));
